// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Box, Input, Option, Textarea } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import CmsToggle from "../../cms/components/CmsToggle";
import { SDUI_SECTION_CREATE_MUTATION, SDUI_SECTION_UPDATE_MUTATION } from "../sduiMutations";
import { SDUI_SECTIONS_QUERY, STOREFRONT_LAYOUT_QUERY } from "../sduiQueries";
import {
  SDUIAnimationEffect,
  SDUILayoutVariant,
  SDUINavigationTarget,
  SDUISection,
  SDUISectionType,
} from "../types";

interface SectionDialogProps {
  open: boolean;
  section?: SDUISection | null;
  onClose: () => void;
}

const SECTION_TYPES: Option[] = [
  { label: "Carousel Banner Hero", value: "CAROUSEL_BANNER" },
  { label: "Short Video Reels", value: "SHORT_VIDEO" },
  { label: "Seasonal Produce Picks", value: "SEASONAL_PICK" },
  { label: "Product List Carousel/Grid", value: "PRODUCT_LIST" },
  { label: "Category Grid Explorer", value: "CATEGORY_GRID" },
  { label: "Promotional Banner Strip", value: "PROMOTIONAL_STRIP" },
  { label: "Custom Rich Content / HTML", value: "CUSTOM_HTML" },
  { label: "Countdown Flash Sale Timer", value: "COUNTDOWN_TIMER" },
  { label: "Featured Container Group (Nested Parent)", value: "FEATURED_GROUP" },
];

const LAYOUT_VARIANTS: Option[] = [
  { label: "Default Flow (Auto)", value: "DEFAULT" },
  { label: "Grid 4 Columns (Quick Commerce Icons)", value: "GRID_4X2" },
  { label: "Grid 2x2 Big Cards", value: "GRID_2X2" },
  { label: "Horizontal Scroll Row (Snap Cards)", value: "HORIZONTAL_SCROLL" },
  { label: "Slim Banner Strip", value: "BANNER_STRIP" },
  { label: "Full-Bleed Card Slider", value: "CARD_SLIDER" },
  { label: "Featured Hero Billboard", value: "FEATURED_HERO" },
];

const ANIMATION_EFFECTS: Option[] = [
  { label: "None (Clean)", value: "NONE" },
  { label: "🪔 Festive Diyas & Sparkles (Diwali)", value: "FESTIVAL_DIYAS" },
  { label: "🎉 Celebration Confetti", value: "CONFETTI" },
  { label: "🍃 Monsoon / Harvest Falling Leaves", value: "FALLING_LEAVES" },
  { label: "✨ Golden Shimmer & Glow", value: "GOLDEN_SPARKLES" },
  { label: "⚡ 10-Min Delivery Speed Pulse", value: "PULSE_BADGE" },
  { label: "💫 Shimmer Light Sweep (Blinkit Style)", value: "SHIMMER_SWEEP" },
  { label: "🌟 Spotlight Pulsing Glow Border (Zepto Style)", value: "PULSE_BORDER" },
  { label: "🏷️ Attention Wiggle & Bounce (Instamart)", value: "BOUNCE_WIGGLE" },
  { label: "🛵 Animated Fast Delivery Motion (GIF/Rider)", value: "BANNER_GIF_RIDER" },
];

const NAV_TARGETS: Option[] = [
  { label: "None (Static Display)", value: "NONE" },
  { label: "Category Page", value: "CATEGORY" },
  { label: "Product Details", value: "PRODUCT" },
  { label: "Collection Listing", value: "COLLECTION" },
  { label: "🕒 Delivery Slot Drawer (Bottom Sheet)", value: "DRAWER_DELIVERY_SLOTS" },
  { label: "💰 Quick Wallet Top-up Drawer", value: "DRAWER_WALLET_TOPUP" },
  { label: "🛒 Cart Preview Drawer", value: "DRAWER_CART_PREVIEW" },
  { label: "🎬 Vertical Shorts Reel", value: "SHORTS_REEL" },
  { label: "External Web URL", value: "EXTERNAL_URL" },
  { label: "Mobile Deep Link (URI)", value: "CUSTOM_DEEP_LINK" },
];

export const SectionDialog: React.FC<SectionDialogProps> = ({ open, section, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!section;

  const { data: secData } = useQuery(SDUI_SECTIONS_QUERY);
  const potentialParents = (secData?.sduiSections || []).filter(
    (s: SDUISection) => !section || s.id !== section.id
  );

  const parentOptions: Option[] = [
    { label: "None (Top-Level Section)", value: "none" },
    ...potentialParents.map((p: SDUISection) => ({
      label: `${p.label} (${p.sectionType})`,
      value: p.id,
    })),
  ];

  const [sectionKey, setSectionKey] = useState(section?.sectionKey || "");
  const [label, setLabel] = useState(section?.label || "");
  const [sectionType, setSectionType] = useState<SDUISectionType>(
    section?.sectionType || "CAROUSEL_BANNER"
  );
  const [layoutVariant, setLayoutVariant] = useState<SDUILayoutVariant>(
    section?.layoutVariant || "DEFAULT"
  );
  const [animationEffect, setAnimationEffect] = useState<SDUIAnimationEffect>(
    section?.animationEffect || "NONE"
  );
  const [spotlightItem, setSpotlightItem] = useState("");
  const [gifBannerUrl, setGifBannerUrl] = useState("");
  const [navTargetType, setNavTargetType] = useState<SDUINavigationTarget>(
    section?.navTargetType || "NONE"
  );
  const [navTargetId, setNavTargetId] = useState(section?.navTargetId || "");
  const [badgeText, setBadgeText] = useState(section?.badgeText || "");
  const [badgeColor, setBadgeColor] = useState(section?.badgeColor || "#e11d48");
  const [parentId, setParentId] = useState(section?.parentId ? section.parentId : "none");
  const [config, setConfig] = useState("");
  const [isPublished, setIsPublished] = useState(section ? section.isPublished : true);

  React.useEffect(() => {
    if (section) {
      setSectionKey(section.sectionKey || "");
      setLabel(section.label || "");
      setSectionType(section.sectionType || "CAROUSEL_BANNER");
      setLayoutVariant(section.layoutVariant || "DEFAULT");
      setAnimationEffect(section.animationEffect || "NONE");
      setNavTargetType(section.navTargetType || "NONE");
      setNavTargetId(section.navTargetId || "");
      setBadgeText(section.badgeText || "");
      setBadgeColor(section.badgeColor || "#e11d48");
      setParentId(section.parentId ? section.parentId : "none");

      let parsedCfg: any = {};
      try {
        parsedCfg = typeof section.config === "string" ? JSON.parse(section.config) : (section.config || {});
      } catch {
        parsedCfg = {};
      }
      setSpotlightItem(parsedCfg.spotlight_item || "");
      setGifBannerUrl(parsedCfg.gif_banner_url || "");
      setConfig(JSON.stringify(parsedCfg, null, 2));
      setIsPublished(section.isPublished ?? true);
    } else {
      setSectionKey("");
      setLabel("");
      setSectionType("CAROUSEL_BANNER");
      setLayoutVariant("DEFAULT");
      setAnimationEffect("NONE");
      setSpotlightItem("");
      setGifBannerUrl("");
      setNavTargetType("NONE");
      setNavTargetId("");
      setBadgeText("");
      setBadgeColor("#e11d48");
      setParentId("none");
      setConfig(
        JSON.stringify({ title: "Featured Section", subtitle: "Explore today's harvest" }, null, 2)
      );
      setIsPublished(true);
    }
  }, [section, open]);

  const [createSection, { loading: creating }] = useMutation(SDUI_SECTION_CREATE_MUTATION, {
    refetchQueries: [
      { query: SDUI_SECTIONS_QUERY },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "APP" } },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "WEB" } },
    ],
    onCompleted: data => {
      if (data.sduiSectionCreate?.errors?.length) {
        notify({ status: "error", text: data.sduiSectionCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Homepage section created successfully" });
        onClose();
      }
    },
  });

  const [updateSection, { loading: updating }] = useMutation(SDUI_SECTION_UPDATE_MUTATION, {
    refetchQueries: [
      { query: SDUI_SECTIONS_QUERY },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "APP" } },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "WEB" } },
    ],
    onCompleted: data => {
      if (data.sduiSectionUpdate?.errors?.length) {
        notify({ status: "error", text: data.sduiSectionUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Homepage section updated successfully" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!sectionKey || !label) {
      notify({ status: "error", text: "Section key and label are required" });
      return;
    }

    let parsedConfig: any = {};
    try {
      parsedConfig = config ? JSON.parse(config) : {};
    } catch {
      notify({ status: "error", text: "Invalid JSON in section config" });
      return;
    }

    if (spotlightItem) {
      parsedConfig.spotlight_item = spotlightItem;
    } else {
      delete parsedConfig.spotlight_item;
    }

    if (gifBannerUrl) {
      parsedConfig.gif_banner_url = gifBannerUrl;
    } else {
      delete parsedConfig.gif_banner_url;
    }

    const finalConfigStr = JSON.stringify(parsedConfig);

    const input: any = {
      sectionKey,
      label,
      sectionType,
      layoutVariant,
      animationEffect,
      navTargetType,
      navTargetId,
      badgeText,
      badgeColor,
      parentId: parentId === "none" ? null : parentId,
      config: finalConfigStr,
      isPublished,
    };

    if (isEdit) {
      updateSection({ variables: { id: section.id, input } });
    } else {
      createSection({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit 2026 Dynamic Section" : "Add Dynamic Homepage Section"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        {/* Key & Label */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Input
            label="Section Key (Unique identifier)"
            value={sectionKey}
            onChange={e => setSectionKey(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. hero_carousel, express_categories"
            helperText="Internal code identifier for layout queries"
            width="100%"
            required
          />
          <Input
            label="Display Label (Admin Title)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. Today's Fresh Harvest Deals"
            helperText="Descriptive label visible in dashboard"
            width="100%"
            required
          />
        </Box>

        {/* Component Type & Layout Variant */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Select
            label="Component Type"
            name="sectionType"
            value={sectionType}
            options={SECTION_TYPES}
            onChange={({ target }) => setSectionType(target.value as SDUISectionType)}
          />
          <Select
            label="Visual Layout Variant"
            name="layoutVariant"
            value={layoutVariant}
            options={LAYOUT_VARIANTS}
            onChange={({ target }) => setLayoutVariant(target.value as SDUILayoutVariant)}
          />
        </Box>

        {/* Animation Effect & Badge */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Select
            label="Section & Card Animation Effect"
            name="animationEffect"
            value={animationEffect}
            options={ANIMATION_EFFECTS}
            onChange={({ target }) => setAnimationEffect(target.value as SDUIAnimationEffect)}
          />
          <Box display="flex" gap={2} alignItems="flex-start">
            <Input
              label="Badge Text"
              value={badgeText}
              onChange={e => setBadgeText(e.target.value)}
              placeholder="e.g. ⚡ 10 MINS, 50% OFF"
              helperText="Optional pill badge on section header"
              width="100%"
            />
            <Box display="flex" flexDirection="column" style={{ minWidth: 80, paddingTop: 2 }}>
              <Typography style={{ fontSize: "0.75rem", color: "#666", fontWeight: 600, marginBottom: 6 }}>
                Badge Color
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input
                  type="color"
                  value={badgeColor}
                  onChange={e => setBadgeColor(e.target.value)}
                  style={{ width: 40, height: 38, borderRadius: 6, border: "1px solid #D1D5DB", cursor: "pointer", padding: 2 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 2026 Spotlight Highlight Controls */}
        <Box
          style={{
            backgroundColor: "#FEFCE8",
            border: "1px solid #FEF08A",
            borderRadius: 10,
            padding: "14px 16px",
          }}
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#854D0E" }}>
            ✨ Spotlight Category / Product & Banner Motion (Blinkit & Zepto Style)
          </Typography>

          <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Input
              label="Spotlight Item Key or Title (Highlights card)"
              value={spotlightItem}
              onChange={e => setSpotlightItem(e.target.value)}
              placeholder="e.g. 'vegetables', 'Purandar Organic Palak'"
              helperText="Card to apply neon glow / border animation"
              width="100%"
            />
            <Input
              label="Banner GIF / Animated Graphic URL"
              value={gifBannerUrl}
              onChange={e => setGifBannerUrl(e.target.value)}
              placeholder="https://media.giphy.com/..."
              helperText="Optional animated GIF banner URL"
              width="100%"
            />
          </Box>
        </Box>

        {/* Interactive Navigation & Drawer Trigger */}
        <Box
          style={{
            backgroundColor: "#F0F9FF",
            border: "1px solid #BAE6FD",
            borderRadius: 10,
            padding: "14px 16px",
          }}
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#0369A1" }}>
            🚀 Interactive Tap & Quick-Commerce Action
          </Typography>

          <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Select
              label="On-Click Action / Drawer"
              name="navTargetType"
              value={navTargetType}
              options={NAV_TARGETS}
              onChange={({ target }) => setNavTargetType(target.value as SDUINavigationTarget)}
            />
            <Input
              label="Destination ID / Slug / Deep Link"
              value={navTargetId}
              onChange={e => setNavTargetId(e.target.value)}
              placeholder="e.g. express-delivery-slots, fresh-veggies"
              helperText="Target identifier, collection slug or drawer action"
              width="100%"
            />
          </Box>
        </Box>

        {/* Parent Container (Nesting) */}
        <Select
          label="Parent Section Container (For Nested Tree / Groups)"
          name="parentId"
          value={parentId}
          options={parentOptions}
          onChange={({ target }) => setParentId(target.value)}
        />

        {/* Collapsible Advanced Config (JSON) */}
        <Accordion style={{ border: "1px solid #E5E7EB", borderRadius: 8, boxShadow: "none" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" style={{ fontWeight: 600, color: "#4B5563" }}>
              ⚙️ Advanced Component Props (JSON)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Textarea
              label="Custom JSON Props & Query Filters"
              value={config}
              onChange={e => setConfig(e.target.value)}
              rows={5}
              width="100%"
              helperText="Component props, data query limits, view-all text, custom styles"
            />
          </AccordionDetails>
        </Accordion>

        <CmsToggle
          label="Published & Rendered on Storefront"
          description="Make this component live and interactive on mobile & web clients"
          checked={isPublished}
          onChange={setIsPublished}
        />
      </Box>
    </ActionDialog>
  );
};

export default SectionDialog;
